import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { AgenticTestResultBadge, TagIconBadge } from "../../components/badge";
import { Button, ButtonRow, DangerButton, PrimaryButton } from "../../components/button";
import { Card, CardContent, CardHeader } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DateTimeLabel } from "../../components/datetime-label";
import { Dropdown, DropdownItem } from "../../components/dropdown";
import { FormControl, Select, TextInput } from "../../components/form";
import { Icon } from "../../components/icon";
import { Section } from "../../components/layout";
import { ExternalLink, Link } from "../../components/link";
import {
    DefaultRow,
    Table,
    TableActionCell,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderActionCell,
    TableHeaderCell,
    TableRow
} from "../../components/table";
import { CopiableText, MutedText, Paragraph } from "../../components/text";
import { closeFlyout, showFlyout } from "../../ui/flyout";
import { formatFlexibibleCurrency, formatFloat } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { throttledCall } from "../../ui/throttle";
import { cond } from "../../util";

export default function (ctx) {
    const search = el.state.create(ctx.query.search || "");
    const gateway = el.state.create(ctx.query.gateway || "");
    const gateways = el.state.create([]);
    const tests = el.state.create(null);

    async function refresh() {
        tests.value = (await api.getJson(
            `/api/v1/agentic-tests?${new URLSearchParams({
                search: search.value,
                gateway: gateway.value
            })}`
        )).data;
    }

    runOperation("Loading Agentic Tests...", async () => {
        gateways.value = (await api.getJson("/api/v1/ai-gateways")).data;
        await refresh();
    });

    return Dashboard(
        DashboardTitle(
            "Agentic Tests",
            el.fragment(
                "Create repeatable simulations to evaluate how your AI gateways behave over time. Read the ",
                ExternalLink(
                    "documentation",
                    "https://docs.aivax.net/docs/inference/agentic-tests"
                ),
                "."
            )
        ),
        Section(
            CommandBar(
                Dropdown(
                    "Create",
                    DropdownItem(
                        "New test",
                        Icon("add-line"),
                        {
                            onClick() {
                                page("/dashboard/tests/create");
                            }
                        }
                    ),
                    DropdownItem(
                        "Open playground",
                        Icon("play-circle-line"),
                        {
                            onClick() {
                                page("/dashboard/tests/playground");
                            }
                        }
                    )
                ),
                Button(
                    Icon("refresh-line"),
                    "Refresh",
                    {
                        onClick: refresh
                    }
                )
            )
        ),
        Section(
            CommandBar(
                FormControl(
                    "Search",
                    TextInput(
                        "agenticTestSearch",
                        "Search tests...",
                        {
                            value: search.value,
                            onInput(event) {
                                throttledCall(() => {
                                    search.value = event.target.value;
                                    refresh();
                                }, 300);
                            }
                        }
                    )
                ),
                FormControl(
                    "AI gateway",
                    el.state.listening([gateways, gateway], () =>
                        Select({
                            value: gateway.value,
                            items: [
                                {
                                    value: "",
                                    label: "All gateways"
                                },
                                ...gateways.value.map(item => ({
                                    value: item.id,
                                    label: item.name
                                }))
                            ],
                            onChange(event) {
                                gateway.value = event.target.value;
                                refresh();
                            }
                        })
                    )
                )
            )
        ),
        Section(
            Card(
                { class: "bg-card" },
                CardHeader("Tests"),
                CardContent(
                    el.state.listening([tests, gateways], () =>
                        Table(
                            {
                                classList: ["hoverable"]
                            },
                            TableHeader(
                                TableHeaderCell("Name"),
                                TableHeaderCell("Gateway"),
                                TableHeaderCell("Schedule"),
                                TableHeaderCell("Result"),
                                TableHeaderCell("Score"),
                                TableHeaderCell("Cost"),
                                TableHeaderActionCell("Actions")
                            ),
                            TableBody(
                                tests.value?.map(test =>
                                    TableRow(
                                        TableCell(
                                            test.name,
                                            el(
                                                "div",
                                                CopiableText(
                                                    `#${test.id.slice(-4)}`,
                                                    test.id
                                                )
                                            )
                                        ),
                                        TableCell(
                                            gateways.value
                                                .filter(item => item.id === test.gateway)
                                                .map(item => Link(
                                                    item.name,
                                                    `/dashboard/gateways/${item.id}`
                                                ))[0]
                                            || test.gateway
                                            || MutedText("No gateway")
                                        ),
                                        TableCell(
                                            test.enabled && test.cron
                                                ? el.fragment(
                                                    CopiableText(test.cron),
                                                    test.next_run_at && el(
                                                        "div",
                                                        DateTimeLabel(test.next_run_at)
                                                    )
                                                )
                                                : MutedText("Manual")
                                        ),
                                        TableCell(
                                            test.latest_run
                                                ? el.fragment(
                                                    AgenticTestResultBadge(
                                                        test.latest_run
                                                    ),
                                                    el(
                                                        "div",
                                                        DateTimeLabel(
                                                            test.latest_run.finished_at
                                                            || test.latest_run.created_at
                                                        )
                                                    )
                                                )
                                                : MutedText("Never run")
                                        ),
                                        TableCell(
                                            test.latest_run?.score != null
                                                ? test.latest_run.score >= 0
                                                    ? TagIconBadge(
                                                        Icon(test.latest_run.score >= 0.96 ? "check-line" : "alert-line"),
                                                        formatFloat(test.latest_run.score),
                                                        test.latest_run.score >= 0.96 ? "var(--success)" : "var(--warn)"
                                                    )
                                                    : formatFloat(test.latest_run.score)
                                                : MutedText("-")
                                        ),
                                        TableCell(
                                            { title: "Total cost of the latest run of this test, including the simulated user, tested gateway/model, and judge. Not an average or cumulative cost across runs." },
                                            test.latest_run?.cost != null
                                                ? formatFlexibibleCurrency(test.latest_run.cost)
                                                : MutedText("-")
                                        ),
                                        TableActionCell(
                                            ButtonRow(
                                                Button(
                                                    Icon("arrow-right-double-line"),
                                                    "View test",
                                                    {
                                                        onClick() {
                                                            page(`/dashboard/tests/${test.id}`);
                                                        }
                                                    }
                                                ),
                                                Dropdown(
                                                    "More",
                                                    DropdownItem(
                                                        "Run test",
                                                        Icon("play-line"),
                                                        {
                                                            onClick() {
                                                                showFlyout(
                                                                    Paragraph(
                                                                        `Run “${test.name}” now? `
                                                                        + "This execution uses your account balance."
                                                                    ),
                                                                    PrimaryButton(
                                                                        Icon("play-fill"),
                                                                        "Run test",
                                                                        {
                                                                            onClick(event) {
                                                                                runOperation(
                                                                                    "Starting Agentic Test...",
                                                                                    async () => {
                                                                                        const response = await api.postJson(
                                                                                            `/api/v1/agentic-tests/${test.id}/runs`,
                                                                                            {}
                                                                                        );
                                                                                        closeFlyout(event.target);
                                                                                        page(
                                                                                            "/dashboard/tests/"
                                                                                            + `${test.id}/runs/`
                                                                                            + response.data.id
                                                                                        );
                                                                                    }
                                                                                );
                                                                            }
                                                                        }
                                                                    )
                                                                );
                                                            }
                                                        }
                                                    ),
                                                    DropdownItem(
                                                        "Delete test",
                                                        Icon("delete-bin-line"),
                                                        {
                                                            onClick() {
                                                                showFlyout(
                                                                    Paragraph(
                                                                        `Delete “${test.name}”? This action cannot be undone.`
                                                                    ),
                                                                    DangerButton(
                                                                        Icon("delete-bin-line"),
                                                                        "Delete test",
                                                                        {
                                                                            onClick(event) {
                                                                                runOperation(
                                                                                    "Deleting Agentic Test...",
                                                                                    async () => {
                                                                                        await api.deleteJson(
                                                                                            `/api/v1/agentic-tests/${test.id}`
                                                                                        );
                                                                                        closeFlyout(event.target);
                                                                                        await refresh();
                                                                                    }
                                                                                );
                                                                            }
                                                                        }
                                                                    )
                                                                );
                                                            }
                                                        }
                                                    )
                                                )
                                            )
                                        )
                                    )
                                )?.nullWhenEmpty()
                                || DefaultRow("No Agentic Tests found.")
                            )
                        )
                    )
                )
            )
        )
    );
}
